import React, { useMemo } from 'react';
import { ActivityIndicator, RefreshControl, View } from 'react-native';
import { router } from 'expo-router';

import { DashboardScrollView } from '@/components/dashboard-scroll-view';
import { ThemedText } from '@/components/themed-text';
import { DetailHeader } from '@/components/ui/detail-header';
import { EmptyState } from '@/components/ui/empty-state';
import { AppointmentCard } from '@/components/schedule/appointment-card';
import { PendingRecurrenceCard } from '@/components/schedule/pending-recurrence-card';
import { RecurrenceAppointmentGroup } from '@/components/schedule/recurrence-appointment-group';
import { useClientAppointments } from '@/hooks/use-appointments';
import { useMyRecurrences } from '@/hooks/use-recurrences';
import { Appointment, APPOINTMENT_STATUS } from '@/types/appointment';
import { ENROLLMENT_STATUS } from '@/types/enrollment-status';
import { groupAppointmentsByRecurrence } from '@/utils/group-appointments';

const CLOSED = [APPOINTMENT_STATUS.CANCELLED];

export default function ClientAppointmentsScreen() {
  const { data, isLoading, refetch, isRefetching } = useClientAppointments();
  const { data: recurrences, refetch: refetchRecurrences } = useMyRecurrences();

  const pendingRecurrences = useMemo(
    () => (recurrences ?? []).filter((r) => r.status === ENROLLMENT_STATUS.PENDING_APPROVAL),
    [recurrences],
  );

  const { upcoming, past } = useMemo(() => {
    const list = data ?? [];
    const now = Date.now();
    const upcoming: Appointment[] = [];
    const past: Appointment[] = [];
    for (const a of list) {
      const isFuture = new Date(a.startAt).getTime() > now;
      if (isFuture && !CLOSED.includes(a.status)) upcoming.push(a);
      else past.push(a);
    }
    upcoming.sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
    return { upcoming, past };
  }, [data]);

  const upcomingGroups = useMemo(() => groupAppointmentsByRecurrence(upcoming), [upcoming]);
  const pastGroups = useMemo(() => groupAppointmentsByRecurrence(past), [past]);

  const openAppointment = (appointment: Appointment) =>
    router.push({
      pathname: '/(tabs)/(client)/appointments/[id]',
      params: { id: appointment.id },
    });

  return (
    <View className="flex-1 bg-canvas">
      <DashboardScrollView
        className="w-full"
        contentContainerStyle={{ paddingBottom: 80 }}
        refreshControl={
          <RefreshControl
            refreshing={isRefetching}
            onRefresh={() => {
              void refetch();
              void refetchRecurrences();
            }}
          />
        }
      >
        <View className="gap-6 p-5">
          <DetailHeader className="px-0" title="Meus agendamentos" onBack={() => router.back()} />

          {isLoading ? (
            <View className="items-center py-16">
              <ActivityIndicator size="large" color="#FD5001" />
            </View>
          ) : upcoming.length === 0 && past.length === 0 && pendingRecurrences.length === 0 ? (
            <EmptyState
              icon="calendar-outline"
              title="Nenhum agendamento ainda"
              description="Escolha um prestador e marque seu primeiro horário."
              actionLabel="Agendar horário"
              onAction={() => router.push('/(tabs)/(client)/booking')}
            />
          ) : (
            <>
              {pendingRecurrences.length > 0 && (
                <View className="gap-3">
                  <ThemedText className="font-semibold text-sm text-neutral-500">
                    Aguardando aprovação
                  </ThemedText>
                  {pendingRecurrences.map((item) => (
                    <PendingRecurrenceCard key={item.id} item={item} />
                  ))}
                </View>
              )}

              {upcoming.length > 0 && (
                <View className="gap-3">
                  <ThemedText className="font-semibold text-sm text-neutral-500">
                    Próximos
                  </ThemedText>
                  {upcomingGroups.map((group) =>
                    group.type === 'single' ? (
                      <AppointmentCard
                        key={group.appointment.id}
                        appointment={group.appointment}
                        onOpen={openAppointment}
                      />
                    ) : (
                      <RecurrenceAppointmentGroup
                        key={group.enrollmentId}
                        enrollmentId={group.enrollmentId}
                        appointments={group.appointments}
                        onOpen={openAppointment}
                        canBook
                      />
                    ),
                  )}
                </View>
              )}

              {past.length > 0 && (
                <View className="gap-3">
                  <ThemedText className="font-semibold text-sm text-neutral-500">
                    Histórico
                  </ThemedText>
                  {pastGroups.map((group) =>
                    group.type === 'single' ? (
                      <AppointmentCard
                        key={group.appointment.id}
                        appointment={group.appointment}
                        onOpen={openAppointment}
                      />
                    ) : (
                      <RecurrenceAppointmentGroup
                        key={group.enrollmentId}
                        enrollmentId={group.enrollmentId}
                        appointments={group.appointments}
                        onOpen={openAppointment}
                      />
                    ),
                  )}
                </View>
              )}
            </>
          )}
        </View>
      </DashboardScrollView>
    </View>
  );
}
